import { HasInternalRole, useAccount } from "@qw/auth";
import { isInternal } from "@qw/qw-common";
import { Button, cn, Icon, Popover, PopoverAnchor, PopoverContent, PopoverTrigger, Spinner } from "@qw/ui";
import { useId, useMemo, useRef, useState } from "react";
import { useInsuredFiles } from "../../hooks";
import { type FolderOption, flattenFolders, folderSuggestions } from "../../util";
import { FilesFolders } from "./files-folders";
import { FilesOptions } from "./files-options";

const FoldersMenu = () => {
  const { foldersVisible, setFoldersVisible } = useInsuredFiles();
  return (
    <Popover open={foldersVisible} onOpenChange={setFoldersVisible}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          display="icon"
          aria-label="Folders"
          // Sized like the app nav's collapse button: an 18px muted icon, no border.
          className="flex-none size-8 text-muted-foreground hover:text-foreground [&>.icon]:mx-0 [&>.icon]:text-lg"
        >
          <Icon icon={foldersVisible ? "folder_open" : "folder"} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[min(28rem,var(--radix-popover-content-available-height))] w-72 overflow-y-auto p-0"
      >
        <FilesFolders />
      </PopoverContent>
    </Popover>
  );
};

const ClearButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button
    variant="unstyled"
    size="xs"
    display="icon"
    aria-label={label}
    className="flex-none text-muted-foreground text-sm"
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
  >
    <Icon icon="close" />
  </Button>
);

// Search doubles as a folder picker: matching folders are suggested as you type, and a picked folder
// sits in the field as a chip so the query runs within it.
const FilesSearch = () => {
  const { user } = useAccount();
  const { query, selectedTags, setQuery, setSelectedTags, tagTree } = useInsuredFiles();
  const listId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const folders = useMemo(() => flattenFolders(tagTree), [tagTree]);
  const suggestions = isInternal(user)
    ? folderSuggestions(folders, query).filter(({ label }) => label !== selectedTags)
    : [];
  const open = suggesting && suggestions.length > 0;
  const active = open ? suggestions[Math.min(activeIndex, suggestions.length - 1)] : undefined;

  const pickFolder = (folder: FolderOption) => {
    setSelectedTags(folder.label);
    setQuery("");
    setSuggesting(false);
  };

  return (
    <Popover open={open} onOpenChange={setSuggesting}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="flex h-8 items-center gap-2 rounded-3xl bg-foreground/5 pl-3 pr-1 text-xs">
          <Icon icon="search" className="flex-none text-muted-foreground text-sm" />
          {selectedTags && (
            <span className="flex min-w-0 max-w-[50%] flex-none items-center gap-1 rounded-full bg-background py-0.5 pl-2 pr-1">
              <Icon icon="folder" className="flex-none text-sm" />
              <span className="truncate">{selectedTags}</span>
              <ClearButton label={`Clear ${selectedTags} folder`} onClick={() => setSelectedTags("")} />
            </span>
          )}
          <input
            type="search"
            role="combobox"
            aria-label={selectedTags ? `Search ${selectedTags}` : "Search files"}
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listId}
            aria-activedescendant={active ? `${listId}-${active.label}` : undefined}
            className="min-w-0 flex-1 bg-transparent border-0 h-8 py-2 text-xs focus-visible:outline-hidden placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
            placeholder={selectedTags ? `Search ${selectedTags}` : "Search files"}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSuggesting(true);
              setActiveIndex(0);
            }}
            onFocus={() => setSuggesting(true)}
            onBlur={() => setSuggesting(false)}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                if (!open) {
                  return;
                }
                event.preventDefault();
                const step = event.key === "ArrowDown" ? 1 : -1;
                setActiveIndex((index) => (index + step + suggestions.length) % suggestions.length);
              } else if (event.key === "Enter" && active) {
                event.preventDefault();
                pickFolder(active);
              } else if (event.key === "Escape" && open) {
                event.preventDefault();
                setSuggesting(false);
              } else if (event.key === "Backspace" && !query && selectedTags) {
                setSelectedTags("");
              }
            }}
          />
          {query && <ClearButton label="Clear search" onClick={() => setQuery("")} />}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) min-w-56 p-1"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
      >
        <div id={listId} role="listbox" aria-label="Folders">
          {suggestions.map((folder) => (
            <div
              key={folder.label}
              id={`${listId}-${folder.label}`}
              role="option"
              aria-selected={folder === active}
              tabIndex={-1}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs",
                folder === active && "bg-foreground/5"
              )}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(suggestions.indexOf(folder))}
              onClick={() => pickFolder(folder)}
            >
              <Icon icon="folder" className="flex-none text-sm text-muted-foreground" />
              <span className="truncate">{folder.label}</span>
              {folder.parent && <span className="ml-auto flex-none text-muted-foreground">in {folder.parent}</span>}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const FilesHeader = () => {
  const { loading } = useInsuredFiles();
  return (
    <header className="flex flex-none items-center gap-3 border-b px-6 py-5">
      <HasInternalRole>
        <FoldersMenu />
      </HasInternalRole>
      <div className="min-w-0 flex-1">
        <FilesSearch />
      </div>
      {loading && <Spinner />}
      <div className="flex-none">
        <FilesOptions />
      </div>
    </header>
  );
};
